import React from 'react';

interface FilterOptions {
  spaceType: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface FilterControlsProps {
  filters: FilterOptions;
  onFilterChange: (filters: FilterOptions) => void;
  onClearFilters: () => void;
  totalPlans: number;
  filteredPlans: number;
}

const FilterControls = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  totalPlans, 
  filteredPlans 
}: FilterControlsProps) => {
  const spaceTypes = [
    { value: '', label: 'All Space Types' },
    { value: 'balcony', label: 'Balcony' },
    { value: 'yard', label: 'Small Yard' },
    { value: 'rooftop', label: 'Rooftop' },
    { value: 'indoor', label: 'Indoor Space' },
    { value: 'garden', label: 'Community Garden' },
    { value: 'patio', label: 'Patio' },
    { value: 'terrace', label: 'Terrace' },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'active', label: 'Active' },
    { value: 'completed', label: 'Completed' },
    { value: 'paused', label: 'Paused' },
  ];

  const sortOptions = [
    { value: 'createdAt', label: 'Date Created' },
    { value: 'name', label: 'Plan Name' },
    { value: 'income', label: 'Income Potential' },
  ];

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    onFilterChange({
      ...filters,
      [key]: value,
    });
  };

  const toggleSortOrder = () => {
    onFilterChange({
      ...filters,
      sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  const hasActiveFilters = filters.spaceType || filters.status;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Space Type Filter */}
          <div className="flex-1 min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Space Type
            </label>
            <select
              value={filters?.spaceType || ''}
              onChange={(e) => handleFilterChange('spaceType', e.target.value)}
              className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {spaceTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Controls */}
          <div className="flex-1 min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort By
            </label>
            <div className="flex">
              <select
                value={filters?.sortBy || 'createdAt'}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-l-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                onClick={toggleSortOrder}
                className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200 transition-colors duration-200"
                title={`Sort ${filters?.sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
              >
                <svg 
                  className="w-4 h-4 text-gray-600"
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  {filters?.sortOrder === 'asc' ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8V20m0 0l4-4m-4 4l-4-4M7 4v16m0 0l-4-4m4 4l4-4" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Clear Filters & Results Count */}
        <div className="flex items-center justify-between lg:justify-end gap-4">
          <div className="text-sm text-gray-600">
            Showing {filteredPlans} of {totalPlans} plans
          </div>
          
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors duration-200"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Clear Filters</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterControls;